import React from 'react';
import { FormHelperText } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { makeStyles } from 'tss-react/mui';
import { DateTime } from 'luxon';
import { useLocales, useTheme } from '../../../../hooks';
import FormControl from '../../FormControl';
import ClearableDatePicker from '../../ClearableDatePicker';
import { enrichTestIds } from '../../TestsIds';

interface ContentLabelEndDateFieldProps {
  value?: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

export const useStyles = makeStyles()((theme) => ({
  pickers: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr',
    gap: theme.spacing(2),
    '& .MuiFormControl-root': {
      width: '100%'
    }
  }
}));

// The label stops applying at a UTC instant. Showing the browser zone turns midnight Z into the
// previous evening in Mexico, and editing the time would then save a local offset instead of Z.
const CONTENT_LABEL_END_DATE_ZONE = 'utc';

function utcEndDate(value?: string): DateTime | undefined {
  if (!value) return undefined;
  const parsed = DateTime.fromISO(value, { setZone: true }).setZone(CONTENT_LABEL_END_DATE_ZONE);
  return parsed.isValid ? parsed : undefined;
}

export function ContentLabelEndDateField({ value, onChange, disabled }: ContentLabelEndDateFieldProps): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const { formControlColor } = useTheme();

  const endDate = utcEndDate(value);

  const onDateChange = (date: string | undefined) => {
    if (!date) {
      onChange('');
      return;
    }
    // The date picker only carries the UTC day, so the UTC time already on screen is kept
    const day = DateTime.fromISO(date, { zone: CONTENT_LABEL_END_DATE_ZONE });
    const nextEndDate = (endDate ?? day.startOf('day')).set({ year: day.year, month: day.month, day: day.day });

    onChange(nextEndDate.toISO() ?? '');
  };

  const onTimeChange = (time: DateTime | null) => {
    if (!endDate || !time?.isValid) return;

    const clock = time.setZone(CONTENT_LABEL_END_DATE_ZONE, { keepLocalTime: true });
    onChange(endDate.set({ hour: clock.hour, minute: clock.minute, second: 0, millisecond: 0 }).toISO() ?? '');
  };

  return (
    <FormControl fullWidth data-testid={enrichTestIds.contentLabelEndDate}>
      <div className={classes.pickers}>
        <ClearableDatePicker
          label={t('content.drawer.content_labels.end_date')}
          value={endDate?.toISODate() ?? ''}
          onChange={onDateChange}
          disabled={disabled}
        />
        <TimePicker
          label={t('content.drawer.content_labels.end_time')}
          timezone="UTC"
          value={endDate ?? null}
          onChange={onTimeChange}
          // A time has no day of its own to apply to until the date is picked
          disabled={disabled || !endDate}
          slotProps={{
            textField: {
              color: formControlColor,
              'data-testid': enrichTestIds.contentLabelEndTime
            }
          }}
        />
      </div>
      <FormHelperText>{t('content.drawer.content_labels.end_date_helper_text')}</FormHelperText>
    </FormControl>
  );
}
